import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

// Helper function to format price
const formatPrice = (price) => {
  return `$${price.toLocaleString()}`;
};

const PriceRangeSlider = ({
  label,
  minPrice,
  maxPrice,
  onPriceChange,
  minRange = 0,
  maxRange = 10000,
  step = 50
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandle, setDragHandle] = useState(null);
  const sliderRef = useRef(null);

  const handleMouseDown = (e, handle) => {
    setIsDragging(true);
    setDragHandle(handle);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, x / width));
    const newPrice = Math.round((minRange + percentage * (maxRange - minRange)) / step) * step;

    if (dragHandle === 'min') {
      const newMinPrice = Math.min(newPrice, maxPrice - step);
      onPriceChange({ min: newMinPrice, max: maxPrice });
    } else if (dragHandle === 'max') {
      const newMaxPrice = Math.max(newPrice, minPrice + step);
      onPriceChange({ min: minPrice, max: newMaxPrice });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragHandle(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragHandle, minPrice, maxPrice, onPriceChange, minRange, maxRange, step]);

  const minPercentage = ((minPrice - minRange) / (maxRange - minRange)) * 100;
  const maxPercentage = ((maxPrice - minRange) / (maxRange - minRange)) * 100;
  const rangeWidth = maxPercentage - minPercentage;

  return (
    <Box sx={{ marginBottom: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: '#222', marginBottom: 2 }}>
        <span>{formatPrice(minPrice)}</span>
        <span>{formatPrice(maxPrice)}</span>
      </Box>
      <Box
        ref={sliderRef}
        sx={{
          position: 'relative',
          height: 2,
          backgroundColor: '#e5e7eb',
          borderRadius: 3,
          margin: '0 10px',
          cursor: 'pointer'
        }}
      >
        {/* Range fill */}
        <Box sx={{
          position: 'absolute',
          left: `${minPercentage}%`,
          width: `${rangeWidth}%`,
          height: '100%',
          backgroundColor: '#3b82f6',
          borderRadius: 3
        }} />

        {/* Min handle */}
        <Box
          sx={{
            position: 'absolute',
            left: `calc(${minPercentage}% - 10px)`,
            top: '-7px',
            width: 20,
            height: 20,
            backgroundColor: '#fff',
            border: '2px solid #d1d5db',
            borderRadius: '50%',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing'
            }
          }}
          onMouseDown={(e) => handleMouseDown(e, 'min')}
        />

        {/* Max handle */}
        <Box
          sx={{
            position: 'absolute',
            left: `calc(${maxPercentage}% - 10px)`,
            top: '-7px',
            width: 20,
            height: 20,
            backgroundColor: '#fff',
            border: '2px solid #d1d5db',
            borderRadius: '50%',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing'
            }
          }}
          onMouseDown={(e) => handleMouseDown(e, 'max')}
        />
      </Box>
    </Box>
  );
};

export default PriceRangeSlider; 